import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Search,
  User,
  ShoppingBag,
  LayoutDashboard,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Globe,
  Sun,
  Moon,
  Home,
  Sparkles,
  Info,
  BookOpen,
  Phone,
  LogIn,
  LogOut,
  Truck,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import * as productsApi from "../api/productsApi";
import { formatMoney } from "../utils/format";
import { FREE_SHIPPING_THRESHOLD } from "../constants/shipping";
import "./Navbar.css";
import logo from "../assets/images/logoH.png";

export default function Navbar({ cartCount: propCartCount }) {
  const { cartCount: ctxCartCount } = useCart();
  const cartCount = propCartCount !== undefined ? propCartCount : ctxCartCount;
  const { language, isRTL, toggleLanguage, t } = useLanguage();
  const { theme, isDark, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { isAuthenticated, isAdmin, logout } = useAuth();
  const solid = scrolled || !isHome || mobileOpen;
  const [collections, setCollections] = useState([]);

  const drawerRef = useRef(null);
  const hamburgerRef = useRef(null);

  const navLinks = [
    { key: "nav.home", to: "/" },
    { key: "nav.about", to: "/about" },
    { key: "nav.shop", to: "/shop" },
    { key: "nav.collections", to: "/collections" },
    { key: "nav.blog", to: "/blog" },
    { key: "nav.contact", to: "/contact" },
  ];

  const getNavIcon = (key) => {
    switch (key) {
      case "nav.home":
        return <Home size={18} />;
      case "nav.about":
        return <Info size={18} />;
      case "nav.shop":
        return <ShoppingBag size={18} />;
      case "nav.collections":
        return <Sparkles size={18} />;
      case "nav.blog":
        return <BookOpen size={18} />;
      case "nav.contact":
        return <Phone size={18} />;
      default:
        return <Sparkles size={18} />;
    }
  };

  useEffect(() => {
    productsApi
      .fetchCollections()
      .then((data) => setCollections(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error(err);
        setCollections([]);
      });
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile drawer on route navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Handle outside click & Escape key to close mobile menu
  useEffect(() => {
    if (!mobileOpen) return;

    const handleClickOutside = (e) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(e.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(e.target)
      ) {
        setMobileOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        hamburgerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [mobileOpen]);

  // Auto-close on viewport resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900 && mobileOpen) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileOpen]);

  return (
    <nav
      className={`navbar ${solid ? "navbar--solid" : "navbar--transparent"} ${
        mobileOpen ? "navbar--open" : ""
      }`}
    >
      <div className="navbar__inner">
        <Link to="/" className="navbar__logo">
          <div className="navbar__logo-mark">
            <div className="logo-icon">
              <img src={logo} alt="TERRY LAND" />
            </div>
          </div>
          <span className="logo-wordmark">
            <span className="logo-text">TERRY LAND</span>
            <span className="logo-tagline">{t("brand.tagline")}</span>
          </span>
        </Link>

        <ul className="navbar__links">
          {navLinks.map((l) => {
            if (l.key === "nav.collections") {
              return (
                <li key={l.key} className="navbar__dropdown-wrapper">
                  <span
                    className={`navbar__link ${
                      location.pathname.startsWith("/shop/") ? "active" : ""
                    }`}
                    style={{
                      cursor: "default",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {t(l.key)} <ChevronDown size={14} />
                  </span>
                  <div className="navbar__dropdown">
                    <Link to="/shop" className="navbar__dropdown-item">
                      {t("nav.allCollections")}
                    </Link>
                    {Array.isArray(collections) &&
                      collections.map((c) => {
                        const displayName =
                          (isRTL || language === "ar") && c.arabicName?.trim()
                            ? c.arabicName.trim()
                            : c.name;
                        return (
                          <Link
                            key={c.id}
                            to={`/shop/${encodeURIComponent(c.name.toLowerCase())}`}
                            className="navbar__dropdown-item"
                          >
                            {displayName}
                          </Link>
                        );
                      })}
                  </div>
                </li>
              );
            }
            return (
              <li key={l.key}>
                <Link
                  to={l.to}
                  className={`navbar__link ${location.pathname === l.to ? "active" : ""}`}
                >
                  {t(l.key)}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="navbar__actions">
          {/* Language Switcher Button */}
          <button
            type="button"
            className="navbar__lang-btn"
            onClick={(e) => {
              toggleLanguage();
              e.currentTarget.blur();
            }}
            title={t("nav.languageAria")}
            aria-label={t("nav.languageAria")}
          >
            <Globe size={15} className="navbar__lang-globe" />
            <span className="navbar__lang-btn-text">{t("nav.language")}</span>
            <span className="navbar__lang-mobile-label">
              {language === "en" ? "AR" : "EN"}
            </span>
          </button>

          {/* Theme Toggle Button (Desktop only - hidden on mobile header) */}
          <button
            type="button"
            className="navbar__theme-btn navbar__theme-btn--desktop"
            onClick={(e) => {
              toggleTheme();
              e.currentTarget.blur();
            }}
            title={isDark ? t("nav.themeLight") : t("nav.themeDark")}
            aria-label={isDark ? t("nav.themeLight") : t("nav.themeDark")}
          >
            {isDark ? (
              <Sun size={17} className="navbar__theme-icon navbar__theme-icon--sun" />
            ) : (
              <Moon size={17} className="navbar__theme-icon navbar__theme-icon--moon" />
            )}
          </button>

          {isAdmin && (
            <Link
              to="/admin"
              className="navbar__icon navbar__admin"
              aria-label={t("nav.admin")}
              title={t("nav.admin")}
            >
              <LayoutDashboard size={18} />
            </Link>
          )}

          <Link to="/shop" className="navbar__icon" aria-label={t("action.filter")}>
            <Search size={18} />
          </Link>

          <Link
            to={isAuthenticated ? "/account" : "/login"}
            className="navbar__icon"
            aria-label={t("nav.account")}
            title={isAuthenticated ? t("nav.account") : t("nav.signIn")}
          >
            <User size={18} />
          </Link>

          <Link
            to="/cart"
            className="navbar__icon navbar__cart"
            aria-label={t("nav.cart")}
            title={t("nav.cart")}
          >
            <ShoppingBag size={18} />
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </Link>

          <button
            ref={hamburgerRef}
            type="button"
            className={`navbar__hamburger ${mobileOpen ? "is-active" : ""}`}
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation-drawer"
            aria-label={
              mobileOpen
                ? isRTL
                  ? "إغلاق القائمة"
                  : "Close menu"
                : isRTL
                ? "فتح القائمة"
                : "Open menu"
            }
          >
            <span className="navbar__hamburger-box">
              <span className="navbar__hamburger-bar navbar__hamburger-bar--1" />
              <span className="navbar__hamburger-bar navbar__hamburger-bar--2" />
              <span className="navbar__hamburger-bar navbar__hamburger-bar--3" />
            </span>
          </button>
        </div>
      </div>

      {/* ── Mobile Backdrop (closes on click outside) ── */}
      <div
        className={`navbar__mobile-backdrop ${mobileOpen ? "is-open" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      {/* ── Mobile Navigation Drawer ── */}
      <div
        ref={drawerRef}
        id="mobile-navigation-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={isRTL ? "قائمة التنقل" : "Navigation menu"}
        className={`navbar__mobile-drawer ${mobileOpen ? "is-open" : ""}`}
      >
        {/* Quick Header Row: Language & Theme Chips */}
        <div className="navbar__mobile-header">
          <button
            type="button"
            className="navbar__mobile-chip navbar__mobile-chip--lang"
            onClick={(e) => {
              toggleLanguage();
              e.currentTarget.blur();
            }}
            title={t("nav.languageAria")}
            aria-label={t("nav.languageAria")}
          >
            <Globe size={15} className="navbar__mobile-chip-icon" />
            <span className="navbar__mobile-chip-text">{t("nav.language")}</span>
            <span className="navbar__mobile-chip-badge">
              {language === "en" ? "AR" : "EN"}
            </span>
          </button>

          <button
            type="button"
            className="navbar__mobile-chip navbar__mobile-chip--theme"
            onClick={(e) => {
              toggleTheme();
              e.currentTarget.blur();
            }}
            title={isDark ? t("nav.themeLight") : t("nav.themeDark")}
            aria-label={isDark ? t("nav.themeLight") : t("nav.themeDark")}
          >
            {isDark ? (
              <>
                <Sun size={15} className="navbar__mobile-chip-icon navbar__theme-icon--sun" />
                <span className="navbar__mobile-chip-text">{t("nav.themeLight")}</span>
              </>
            ) : (
              <>
                <Moon size={15} className="navbar__mobile-chip-icon navbar__theme-icon--moon" />
                <span className="navbar__mobile-chip-text">{t("nav.themeDark")}</span>
              </>
            )}
          </button>

          <button
            type="button"
            className="navbar__mobile-close-btn"
            onClick={() => setMobileOpen(false)}
            aria-label={isRTL ? "إغلاق القائمة" : "Close menu"}
            title={isRTL ? "إغلاق" : "Close"}
          >
            <X size={18} />
          </button>
        </div>

        {/* Quick Search trigger */}
        <Link
          to="/shop"
          className="navbar__mobile-search-trigger"
          onClick={() => setMobileOpen(false)}
        >
          <Search size={16} className="navbar__mobile-search-icon" />
          <span className="navbar__mobile-search-placeholder">
            {isRTL ? "البحث في المنتجات والمجموعات…" : "Search products & collections…"}
          </span>
        </Link>

        {/* Main Navigation Links */}
        <div className="navbar__mobile-nav">
          {navLinks.map((l, index) => {
            const isCollections = l.key === "nav.collections";
            const isCollectionActive = location.pathname.startsWith("/shop/");
            const isActive = isCollections ? isCollectionActive : location.pathname === l.to;

            if (isCollections) {
              return (
                <div
                  key={l.key}
                  className={`navbar__mobile-group ${collectionsOpen ? "is-expanded" : ""}`}
                  style={{ "--item-idx": index }}
                >
                  <button
                    type="button"
                    className={`navbar__mobile-item navbar__mobile-accordion-trigger ${
                      isActive ? "active" : ""
                    }`}
                    onClick={() => setCollectionsOpen((prev) => !prev)}
                    aria-expanded={collectionsOpen}
                  >
                    <div className="navbar__mobile-item-left">
                      <span className="navbar__mobile-item-icon">{getNavIcon(l.key)}</span>
                      <span className="navbar__mobile-item-title">{t(l.key)}</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`navbar__mobile-chevron ${collectionsOpen ? "is-rotated" : ""}`}
                    />
                  </button>

                  {/* Accordion subitems */}
                  <div className={`navbar__mobile-accordion ${collectionsOpen ? "is-open" : ""}`}>
                    <Link
                      to="/shop"
                      className={`navbar__mobile-subitem ${
                        location.pathname === "/shop" ? "active" : ""
                      }`}
                      onClick={() => setMobileOpen(false)}
                    >
                      <span className="navbar__mobile-subitem-bullet" />
                      <span>{t("nav.allCollections")}</span>
                    </Link>
                    {Array.isArray(collections) &&
                      collections.map((c) => {
                        const displayName =
                          (isRTL || language === "ar") && c.arabicName?.trim()
                            ? c.arabicName.trim()
                            : c.name;
                        const collPath = `/shop/${encodeURIComponent(c.name.toLowerCase())}`;
                        const isCollActive = location.pathname === collPath;
                        return (
                          <Link
                            key={c.id}
                            to={collPath}
                            className={`navbar__mobile-subitem ${isCollActive ? "active" : ""}`}
                            onClick={() => setMobileOpen(false)}
                          >
                            <span className="navbar__mobile-subitem-bullet" />
                            <span>{displayName}</span>
                          </Link>
                        );
                      })}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={l.key}
                to={l.to}
                className={`navbar__mobile-item ${isActive ? "active" : ""}`}
                onClick={() => setMobileOpen(false)}
                style={{ "--item-idx": index }}
              >
                <div className="navbar__mobile-item-left">
                  <span className="navbar__mobile-item-icon">{getNavIcon(l.key)}</span>
                  <span className="navbar__mobile-item-title">{t(l.key)}</span>
                </div>
                {isRTL ? (
                  <ChevronLeft size={16} className="navbar__mobile-item-arrow" />
                ) : (
                  <ChevronRight size={16} className="navbar__mobile-item-arrow" />
                )}
              </Link>
            );
          })}
        </div>

        <div className="navbar__mobile-divider" />

        {/* User / Account Section */}
        <div className="navbar__mobile-account-section">
          {isAuthenticated ? (
            <div className="navbar__mobile-user-card">
              <Link
                to="/account"
                className="navbar__mobile-item"
                onClick={() => setMobileOpen(false)}
              >
                <div className="navbar__mobile-item-left">
                  <span className="navbar__mobile-item-icon">
                    <User size={18} />
                  </span>
                  <span className="navbar__mobile-item-title">{t("nav.account")}</span>
                </div>
                {isRTL ? (
                  <ChevronLeft size={16} className="navbar__mobile-item-arrow" />
                ) : (
                  <ChevronRight size={16} className="navbar__mobile-item-arrow" />
                )}
              </Link>

              {isAdmin && (
                <Link
                  to="/admin"
                  className="navbar__mobile-item navbar__mobile-admin-item"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="navbar__mobile-item-left">
                    <span className="navbar__mobile-item-icon">
                      <LayoutDashboard size={18} />
                    </span>
                    <span className="navbar__mobile-item-title">{t("nav.admin")}</span>
                  </div>
                  <span className="navbar__mobile-badge">ADMIN</span>
                </Link>
              )}

              <button
                type="button"
                className="navbar__mobile-signout-btn"
                onClick={() => {
                  logout();
                  setMobileOpen(false);
                }}
              >
                <LogOut size={16} />
                <span>{t("account.signOutAction") || (isRTL ? "تسجيل الخروج" : "Sign Out")}</span>
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="navbar__mobile-signin-card"
              onClick={() => setMobileOpen(false)}
            >
              <div className="navbar__mobile-signin-info">
                <span className="navbar__mobile-signin-title">{t("nav.signIn")}</span>
                <span className="navbar__mobile-signin-desc">
                  {isRTL
                    ? "سجل دخولك لتتبع طلباتك وإدارة حسابك"
                    : "Access your orders & saved wishlist"}
                </span>
              </div>
              <div className="navbar__mobile-signin-cta">
                <LogIn size={16} />
              </div>
            </Link>
          )}
        </div>

        {/* Footer reassurance */}
        <div className="navbar__mobile-footer">
          <div className="navbar__mobile-trust-badge">
            <Truck size={14} className="navbar__mobile-trust-icon" />
            <span>
              {t("product.freeDeliveryNote").replace("{amount}", formatMoney(FREE_SHIPPING_THRESHOLD, language))}
            </span>
          </div>
          <div className="navbar__mobile-brand-note">
            TERRY LAND • {t("brand.tagline")}
          </div>
        </div>
      </div>
    </nav>
  );
}
